import React from 'react';
import { FormField, Input, Select } from '../ui';
import { WebsiteConfig, CURRENCIES, TARGET_REGIONS, LANGUAGES } from '../../types';

interface WebsiteConfigFormProps {
  data: WebsiteConfig;
  onChange: (data: Partial<WebsiteConfig>) => void;
  errors: Record<string, string>;
}

const CURRENCY_LABELS: Record<string, string> = {
  USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound', AED: 'UAE Dirham',
  AUD: 'Australian Dollar', NZD: 'New Zealand Dollar', SGD: 'Singapore Dollar',
  HKD: 'Hong Kong Dollar', TWD: 'Taiwan Dollar', JPY: 'Japanese Yen',
  RUB: 'Russian Ruble', KZT: 'Kazakh Tenge', BYN: 'Belarusian Ruble',
  NOK: 'Norwegian Krone', DKK: 'Danish Krone', SEK: 'Swedish Krona',
  TRY: 'Turkish Lira', INR: 'Indian Rupee', ZAR: 'South African Rand',
  PLN: 'Polish Zloty', CZK: 'Czech Koruna', CAD: 'Canadian Dollar',
  BRL: 'Brazilian Real', MXN: 'Mexican Peso', ARS: 'Argentine Peso',
  CLP: 'Chilean Peso', COP: 'Colombian Peso',
};

const currencyOptions = CURRENCIES.map((currency) => ({
  value: currency,
  label: `${currency} - ${CURRENCY_LABELS[currency] || currency}`,
}));

const REGION_LABELS: Record<string, string> = {
  EU: 'European Union', US: 'United States', UK: 'United Kingdom',
  UAE: 'United Arab Emirates', APAC: 'Asia-Pacific', CIS: 'CIS Countries',
  LATAM: 'Latin America', Nordic: 'Nordic Countries', Global: 'Global',
};

const regionOptions = TARGET_REGIONS.map((region) => ({
  value: region,
  label: REGION_LABELS[region] || region,
}));

const languageOptions = LANGUAGES.map((lang) => ({
  value: lang.name,
  label: lang.name,
}));

export const WebsiteConfigForm: React.FC<WebsiteConfigFormProps> = React.memo(({
  data,
  onChange,
  errors,
}) => {
  const handleLanguageChange = (languageName: string) => {
    const language = LANGUAGES.find((l) => l.name === languageName);
    onChange({
      language: languageName,
      languageCode: language?.code || '',
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <FormField
          label="Domain"
          required
          error={errors.domain}
          htmlFor="domain"
          hint="Your website domain (e.g., example.com)"
        >
          <Input
            id="domain"
            type="text"
            value={data.domain}
            onChange={(e) => onChange({ domain: e.target.value.toLowerCase() })}
            placeholder="yourcompany.com"
            error={errors.domain}
          />
        </FormField>

        <FormField
          label="Language"
          required
          error={errors.language}
        >
          <Select
            options={languageOptions}
            value={data.language}
            onChange={handleLanguageChange}
            placeholder="Select language"
            error={errors.language}
          />
        </FormField>

        <FormField
          label="Language Code"
          required
          error={errors.languageCode}
          htmlFor="language-code"
          hint="Auto-filled based on language selection"
        >
          <Input
            id="language-code"
            type="text"
            value={data.languageCode}
            onChange={(e) => onChange({ languageCode: e.target.value })}
            placeholder="en-US"
            error={errors.languageCode}
          />
        </FormField>

        <FormField
          label="Currency"
          required
          error={errors.currency}
        >
          <Select
            options={currencyOptions}
            value={data.currency}
            onChange={(value) => onChange({ currency: value as WebsiteConfig['currency'] })}
            placeholder="Select currency"
            error={errors.currency}
          />
        </FormField>

        <FormField
          label="Target Region"
          required
          error={errors.targetRegion}
          hint="Determines legal compliance requirements"
        >
          <Select
            options={regionOptions}
            value={data.targetRegion}
            onChange={(value) => onChange({ targetRegion: value as WebsiteConfig['targetRegion'] })}
            placeholder="Select target region"
            error={errors.targetRegion}
          />
        </FormField>
      </div>
    </div>
  );
});

WebsiteConfigForm.displayName = 'WebsiteConfigForm';

export default WebsiteConfigForm;
